import { resolveStripeFlags } from "./stripeFeature";
import {
  isConnectedToKjuupPlus,
  resolvePackageId,
  resolvePackageLabel,
} from "./packageTheme";

export const PERSONA_KJUUP = "kjuup";
export const PERSONA_PLUS = "plus";
export const PERSONA_IO = "io";

/**
 * Everything the cockpit / sidebar needs to decide which variant to render.
 *
 * `persona` follows the visual package (`resolvePackageId`):
 *   "kjuup" plain user, "plus" Kjuup+ member, "io" Kjuup.io entitled.
 * A Kjuup.io+ user (io + Kjuup+) has persona "io" and `isKjuupPlusMember`.
 *
 * All inputs are the Redux slices `profile`, `subscription` and `user`.
 */
export function resolveCockpitPersona(profile, subscription, user) {
  const sub = subscription || {};
  const prof = profile || {};
  const usr = user || {};
  const flags = resolveStripeFlags(prof, sub);
  const persona = resolvePackageId(prof, sub);
  const isKjuupPlusMember = isConnectedToKjuupPlus(prof, sub);
  const hasActiveKjuupIoPlan = persona === PERSONA_IO;

  const prefSource = (key) =>
    usr[key] != null ? usr[key] : prof[key] != null ? prof[key] : undefined;
  const chatsEnabledRaw = prefSource("chatsEnabled");
  const emailModeRaw = prefSource("emailMode");
  const teamsModeRaw = prefSource("teamsMode");

  return {
    persona,
    packageLabel: resolvePackageLabel(prof, sub),
    isKjuupPlusMember,
    hasActiveKjuupIoPlan,
    stripeUiEnabled: !!flags.stripeUiEnabled,
    stripeFeatureEnabled: !!flags.stripeFeatureEnabled,
    companyStripeFeatureEnabled: !!flags.companyStripeFeatureEnabled,
    canStartTrial: !!sub.canStartTrial && !sub.active,
    isCompanyAdmin: !!sub.isCompanyAdmin,
    trialing: !!sub.trialing,
    trialDaysRemaining:
      sub.trialDaysRemaining != null ? sub.trialDaysRemaining : sub.daysRemaining,
    companyAdmins: Array.isArray(sub.companyAdmins) ? sub.companyAdmins : [],
    subscriptionLoading: !!sub.loading,
    // Workspace preferences (Kjuup.io). Defaults keep the classic UI.
    chatsEnabled: chatsEnabledRaw == null ? true : chatsEnabledRaw !== false,
    emailMode: String(emailModeRaw || "client").toLowerCase() === "reports" ? "reports" : "client",
    // Microsoft Teams: "client" = Teams chats in Kjuup, "reports" = hidden (daily report only).
    teamsMode: String(teamsModeRaw || "client").toLowerCase() === "reports" ? "reports" : "client",
    kjuupIoSetupDone: !!prefSource("kjuupIoSetupDone"),
  };
}

/** Chats are hidden only for Kjuup.io users who opted out. */
export function chatsHidden(personaInfo) {
  return !!personaInfo && personaInfo.hasActiveKjuupIoPlan && personaInfo.chatsEnabled === false;
}

/** Email inbox UI is hidden when the user runs the room-reports mode. */
export function emailClientHidden(personaInfo) {
  return !!personaInfo && personaInfo.hasActiveKjuupIoPlan && personaInfo.emailMode === "reports";
}

/** Microsoft Teams chats are hidden (Messages, rooms) when the user keeps Teams for the daily report only. */
export function teamsClientHidden(personaInfo) {
  return !!personaInfo && personaInfo.hasActiveKjuupIoPlan && personaInfo.teamsMode === "reports";
}
